// ActiveTaskCard — a task panel with its countdown clock and the task's
// start time above the top line and end time below the bottom line
// (README §6.7.3 item 3, DECISIONS §E.2).
//
// Composition over duplication: the panel body is a normal
// `TaskRectangle`, with the `CountdownDial` slotted onto the LEADING edge
// via `leadingAccessory` — i.e. immediately to the left of the check box.
// When authored windows overlap, several active cards (and so several
// clocks + checkboxes) appear at once. The phase-relative start/end labels
// bracket the rectangle above and below; the wall-clock start/finish reads
// inline in the body as "<start> / <finish>". Purely presentational — the
// cooking pane computes the labels and feeds the clock from `useCountdown`.
//
// `variant`:
// - `active`  — the live task (cream rectangle); the clock counts the
//   task's own duration down and the checkbox is interactive.
// - `pending` — the dormant "next" card shown during an early-finish gap:
//   an oat rectangle with a greyed (disabled) checkbox and a FULL, static
//   ring (the caller passes `remainingSeconds === durationSeconds`). When
//   the task's time arrives the pane re-renders it as `active`.

import { theme } from '../theme';
import { TaskRectangle, type TaskAssistMatcher } from './TaskRectangle';
import { CountdownDial } from './CountdownDial';

export type ActiveTaskCardVariant = 'active' | 'pending';

export interface ActiveTaskCardProps {
  title: string;
  /** Phase-relative start, pre-formatted (e.g. "00:00:00"). Shown above. */
  startLabel: string;
  /** Phase-relative end, pre-formatted (e.g. "00:00:15"). Shown below. */
  endLabel: string;
  /** Whole task window in seconds, for the dial. */
  durationSeconds: number;
  /** Seconds remaining, for the dial. */
  remainingSeconds: number;
  /** Wall-clock HH:MM start, shown inline in the body before " / ". */
  time?: string;
  /** Wall-clock HH:MM finish, shown inline in the body after " / ". */
  endTime?: string;
  lane?: string;
  chefHatRef?: boolean;
  checkboxSide?: 'left' | 'right';
  livecookExpired?: boolean;
  /** `active` (live) or `pending` (dormant up-next during a gap). Default `active`. */
  variant?: ActiveTaskCardVariant;
  /** ISO start instant, passed through for keyboard-nav time-order assertions. */
  startAtIso?: string;
  /** Kitchen Assist matcher for the title (see TaskRectangle). */
  assistMatcher?: TaskAssistMatcher;
  onAssistWord?: (canonicalWord: string) => void;
  onCheck?: () => void;
  onChefHatClick?: () => void;
}

export function ActiveTaskCard({
  title,
  startLabel,
  endLabel,
  durationSeconds,
  remainingSeconds,
  time,
  endTime,
  lane,
  chefHatRef = false,
  checkboxSide = 'left',
  livecookExpired = false,
  variant = 'active',
  startAtIso,
  assistMatcher,
  onAssistWord,
  onCheck,
  onChefHatClick,
}: ActiveTaskCardProps) {
  const pending = variant === 'pending';
  const align = checkboxSide === 'right' ? 'flex-end' : 'flex-start';
  const labelStyle = {
    fontSize: '0.75rem',
    color: theme.text.muted,
    fontVariantNumeric: 'tabular-nums' as const,
    letterSpacing: '0.04em',
    padding: '0 0.25rem',
  };

  return (
    <div
      data-active-task-card=""
      data-variant={variant}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '0.2rem' }}
    >
      <span aria-label={`Start time ${startLabel}`} style={{ ...labelStyle, alignSelf: align }}>
        {startLabel}
      </span>
      <TaskRectangle
        state={pending ? 'upcoming' : 'active'}
        livecookExpired={livecookExpired}
        title={title}
        time={time}
        endTime={endTime}
        startAtIso={startAtIso}
        lane={lane}
        chefHatRef={chefHatRef}
        checkboxSide={checkboxSide}
        checkboxDisabled={pending}
        assistMatcher={assistMatcher}
        onAssistWord={onAssistWord}
        onCheck={pending ? undefined : onCheck}
        onChefHatClick={onChefHatClick}
        leadingAccessory={
          <CountdownDial
            durationSeconds={durationSeconds}
            remainingSeconds={remainingSeconds}
            color={livecookExpired ? theme.accent.late : theme.accent.action}
            trackColor={theme.surface.onAccent}
            label={title}
          />
        }
      />
      <span aria-label={`End time ${endLabel}`} style={{ ...labelStyle, alignSelf: align }}>
        {endLabel}
      </span>
    </div>
  );
}

export default ActiveTaskCard;
